import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { useAuth } from '../state/AuthContext'
import { Hicon, type HiconName } from './Hicon'
import { BrandLogo } from './BrandLogo'

export const DRAWER_WIDTH = 260

type NavItem = {
  to: string
  label: string
  icon: HiconName
  /** Se definida, o item só aparece para quem tem a permissão. */
  /** Uma permissão, ou uma lista (basta ter uma). */
  permissao?: string | string[]
}

/** Os três painéis, os resumos semanais, os alertas, o registro de alterações, o simulador, o chat da IA e os leads dos sites. */
export const NAV_ITEMS: NavItem[] = [
  { to: '/trafego-pago', label: 'Tráfego pago', icon: 'payment' },
  { to: '/organico', label: 'Orgânico', icon: 'graph' },
  { to: '/redes-sociais', label: 'Redes sociais', icon: 'group' },
  { to: '/resumos', label: 'Resumos', icon: 'report' },
  { to: '/alertas', label: 'Alertas', icon: 'bell' },
  { to: '/alteracoes', label: 'Alterações', icon: 'pencil' },
  { to: '/simulador', label: 'Simulador', icon: 'percent' },
  { to: '/chat', label: 'Pergunte à IA', icon: 'activity' },
  { to: '/leads', label: 'Leads', icon: 'user', permissao: 'leads.ver' },
]

export const CONFIG_ITEMS: NavItem[] = [
  { to: '/configuracoes/marcas', label: 'Marcas', icon: 'category', permissao: ['marcas.gerenciar', 'marcas.conectar'] },
  { to: '/configuracoes/conversoes', label: 'Conversões', icon: 'settings', permissao: 'conversoes.gerenciar' },
  { to: '/configuracoes/usuarios', label: 'Usuários', icon: 'user-tag', permissao: 'usuarios.gerenciar' },
  { to: '/configuracoes/papeis', label: 'Papéis', icon: 'document', permissao: 'papeis.gerenciar' },
  { to: '/configuracoes/ia', label: 'IA', icon: 'sync', permissao: 'ia.gerenciar' },
]

function pathActive(pathname: string, to: string) {
  return pathname === to || pathname.startsWith(`${to}/`)
}

/** Mesmos tokens do sidebar do Fiesta BI (DomainNav). */
function sidebarTokens(mode: 'light' | 'dark') {
  if (mode === 'dark') {
    return {
      bg: '#171717',
      text: 'rgba(255, 255, 255, 0.92)',
      muted: '#9e9e9e',
      border: 'rgba(255, 255, 255, 0.1)',
      hover: 'rgba(255, 255, 255, 0.06)',
      activeBg: 'rgba(66, 165, 245, 0.18)',
      accent: '#64b5f6',
      shadow: '1px 0 0 rgba(255, 255, 255, 0.1), 4px 0 28px rgba(0, 0, 0, 0.45)',
    }
  }
  return {
    bg: '#f1f5f9',
    text: '#334155',
    muted: '#64748b',
    border: '#e2e8f0',
    hover: 'rgba(37, 99, 235, 0.08)',
    activeBg: 'rgba(37, 99, 235, 0.12)',
    accent: '#2563eb',
    shadow: '1px 0 0 #e2e8f0, 4px 0 24px rgba(15, 23, 42, 0.05)',
  }
}

type SideNavProps = {
  /** Fecha o drawer temporário no mobile após navegar. */
  onNavigate?: () => void
}

export function SideNav({ onNavigate }: SideNavProps) {
  const theme = useTheme()
  const { pathname } = useLocation()
  const { pode } = useAuth()
  const s = sidebarTokens(theme.palette.mode === 'dark' ? 'dark' : 'light')
  const permitido = (i: NavItem) => !i.permissao || (Array.isArray(i.permissao) ? i.permissao.some(pode) : pode(i.permissao))
  const principais = NAV_ITEMS.filter(permitido)
  const configuracoes = CONFIG_ITEMS.filter(permitido)

  const item = (it: NavItem) => {
    const active = pathActive(pathname, it.to)
    return (
      <ListItem key={it.to} disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          component={RouterLink}
          to={it.to}
          selected={active}
          disableRipple
          onClick={() => onNavigate?.()}
          aria-current={active ? 'page' : undefined}
          sx={{
            mx: 0,
            mr: '0.75rem',
            borderRadius: '0 10px 10px 0',
            py: 1.5,
            px: 3,
            gap: '0.75rem',
            color: active ? s.accent : s.muted,
            fontWeight: active ? 600 : 500,
            transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
            '&:hover': {
              bgcolor: s.hover,
              color: s.text,
            },
            '&.Mui-selected': {
              bgcolor: s.activeBg,
              color: s.accent,
              borderRight: `3px solid ${s.accent}`,
              '&:hover': {
                bgcolor: s.activeBg,
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
            <Hicon name={it.icon} sx={{ fontSize: 22, color: 'inherit' }} />
          </ListItemIcon>
          <ListItemText
            primary={it.label}
            primaryTypographyProps={{
              fontWeight: active ? 600 : 500,
              fontSize: '0.875rem',
              noWrap: true,
              fontFamily: '"Outfit", sans-serif',
            }}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: s.bg,
        color: s.text,
        boxShadow: s.shadow,
        borderRight: 'none',
      }}
    >
      <Box
        sx={{
          minHeight: { xs: 64, sm: 80 },
          px: 2,
          pt: { xs: 1.5, sm: 2 },
          pb: { xs: 1.15, sm: 1.35 },
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
          borderBottom: `1px solid ${s.border}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0, width: '100%' }}>
          <BrandLogo size={22} />
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: s.muted,
              display: 'block',
              lineHeight: 1.2,
              fontWeight: 600,
              letterSpacing: 0.02,
              ml: 0.5,
            }}
          >
            Painéis
          </Typography>
        </Box>
      </Box>

      <List sx={{ flex: 1, overflow: 'auto', py: 2, px: 0 }}>
        {principais.map(item)}
        {configuracoes.length ? (
          <>
            <Typography
              variant="caption"
              component="li"
              sx={{ display: 'block', px: 3, pt: 3, pb: 1, color: s.muted, fontWeight: 600, letterSpacing: 0.02 }}
            >
              Configurações
            </Typography>
            {configuracoes.map(item)}
          </>
        ) : null}
      </List>
    </Box>
  )
}
